import DialogCreatePrompt from "@/components/prompt/dialog-create-prompt";
import DialogSelectPrompt from "@/components/prompt/dialog-select-prompt";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { notifyPromptsChanged } from "@/lib/prompt-events";
import { PromptDTO } from "@shaarp/common";
import { Plus, RefreshCw, X, Zap } from "lucide-react";

type IntegrationCalendarPromptSelectorProps = {
  promptName: string | null;
  isUpdatingPrompt: boolean;
  showPromptDialog: boolean;
  showCreatePromptDialog: boolean;
  onPromptSelect: (prompt: PromptDTO | null) => Promise<void>;
  onShowPromptDialogChange: (show: boolean) => void;
  onShowCreatePromptDialogChange: (show: boolean) => void;
};

export default function IntegrationCalendarPromptSelector({
  promptName,
  isUpdatingPrompt,
  showPromptDialog,
  showCreatePromptDialog,
  onPromptSelect,
  onShowPromptDialogChange,
  onShowCreatePromptDialogChange,
}: IntegrationCalendarPromptSelectorProps) {
  const { t } = useTranslation();

  const handlePromptSelect = (prompt: PromptDTO) => {
    // Only user/team prompts can be selected for calendar integrations
    // Public prompts are templates and should not be used directly
    if (prompt.ownerType !== "public") {
      void onPromptSelect(prompt);
      onShowPromptDialogChange(false);
    }
  };

  return (
    <div className="flex-1 space-y-3">
      <label className="text-sm font-medium">
        {t("team.integrations.googleCalendar.prompt.title")} :
      </label>

      {/* Prompt actif - Card style */}
      <div className="p-4 rounded-lg border bg-muted/30 dark:bg-muted/10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {promptName ? (
                <>
                  <Badge variant="secondary" className="text-xs">
                    {t("team.integrations.googleCalendar.prompt.custom")}
                  </Badge>
                </>
              ) : (
                <>
                  <Zap className="shrink-0 w-4 h-4 text-primary" />
                  <Badge
                    variant="outline"
                    className="text-xs border-primary/20 bg-primary/5"
                  >
                    {t("team.integrations.googleCalendar.prompt.default")}
                  </Badge>
                </>
              )}
            </div>
            <p className="mb-2 text-sm font-medium truncate">
              {promptName ||
                t("team.integrations.googleCalendar.prompt.defaultName")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("team.integrations.googleCalendar.prompt.description")}
            </p>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onShowPromptDialogChange(true)}
                    disabled={isUpdatingPrompt}
                    className="w-8 h-8"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("team.integrations.googleCalendar.prompt.change")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onShowCreatePromptDialogChange(true)}
                    disabled={isUpdatingPrompt}
                    className="w-8 h-8"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("team.integrations.googleCalendar.prompt.create")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {promptName && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPromptSelect(null)}
                      disabled={isUpdatingPrompt}
                      className="w-8 h-8 text-destructive hover:text-destructive"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {t(
                        "team.integrations.googleCalendar.prompt.removePrompt",
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {showPromptDialog && (
        <DialogSelectPrompt
          onSelect={handlePromptSelect}
          onClose={() => onShowPromptDialogChange(false)}
        />
      )}
      {showCreatePromptDialog && (
        <DialogCreatePrompt
          initialContent=""
          onClose={() => onShowCreatePromptDialogChange(false)}
          onCreated={async (prompt: PromptDTO) => {
            // Notify other components about the new prompt
            notifyPromptsChanged();
            await onPromptSelect(prompt);
          }}
        />
      )}
    </div>
  );
}
